const API_BASE = '/api'

const UNSAFE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE']

/** An error carrying the status, for callers that branch on it -- 401 especially. */
export interface ApiError extends Error {
  status: number
}

/** A 401 is a normal answer to "who is signed in", not a failure worth showing. */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof Error && (error as ApiError).status === 401
}

export async function apiFetch<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  // Double-submit: the server compares this against the csrftoken cookie, which
  // is readable precisely so it can be echoed here. A cross-site page carries the
  // cookies but cannot read them, so it cannot produce the header.
  const csrfToken = readCookie('csrftoken')
  if (csrfToken && options.method && UNSAFE_METHODS.includes(options.method)) {
    headers['X-CSRF-Token'] = csrfToken
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    // The session lives in a cookie the page cannot read.
    credentials: 'include',
  })

  if (!response.ok) {
    throw Object.assign(new Error(await messageFor(response)), { status: response.status })
  }

  if (response.status === 204) return undefined as T

  return response.json()
}

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim()
    if (trimmed.startsWith(`${name}=`)) return trimmed.substring(name.length + 1)
  }
  return null
}

/**
 * The department writes its own refusals. A validation failure carries an
 * `errors` array from Dropwizard; anything else carries a `message`. Both are
 * already phrased for the complainant, so they are shown as-is.
 */
async function messageFor(response: Response): Promise<string> {
  const text = await response.text().catch(() => '')
  try {
    const body = JSON.parse(text)
    if (Array.isArray(body.errors) && body.errors.length > 0) return body.errors[0]
    if (body.message) return body.message
  } catch {
    // Not JSON. Fall through to the generic notice.
  }
  if (response.status === 401) return 'Those credentials were not recognised.'
  if (response.status === 404) return 'No complaint is on file under that case number.'
  return 'The form could not be filed. Try again in a moment.'
}
